// Importamos React y hooks
import { useState, useEffect, useRef } from "react";

// Importamos los subcomponentes del Paso 3
import Datos1 from "./validacionesFormularioPaso3/Datos1";
import Datos2 from "./validacionesFormularioPaso3/Datos2";
import Datos3 from "./validacionesFormularioPaso3/Datos3";
import Datos4 from "./validacionesFormularioPaso3/Datos4";

// Tipos @/typescript
import { FormData2, FormErrors, FormularioPaso3Props, PaymentOption } from "@/types";

// Funciones de validación
import { isValidPhone, isValidEmail, isValidPostalCode, ValidateDNI, ValidateCIF, ValidateNIE } from "../../validation/validations";

// Referencia al contrato PDF
import { ContratoPdfRef } from "./contratoServicios/ContratoServicios";

// 🟢 URL base del backend definida en .env
/* import { safeFetch } from "@/api/safeFetch"; */
import { useParams } from "react-router-dom";


// Componente principal del formulario Paso 3
const FormularioPaso3: React.FC<FormularioPaso3Props> = ({
  formData1,               // Datos del paso 1
  setFormData1,            // Función para actualizar datos del paso 1
  selectedProfesiones,     // Profesiones seleccionadas
  paymentOption,           // Opción de pago seleccionada
  isEmpresa,               // Booleano: si es empresa
  handleProfesionSelect,   // Callback para actualizar profesiones
  paymentAmount,           // Monto de pago
  setPaymentDetails,       // Función para actualizar detalles de pago
/*   onPaymentChange,  */        // Callback de cambio de pago
  setPaymentAmount,        // Función para actualizar monto de pago
  setPaymentOption,        // Función para actualizar opción de pago
  onChange,                // Callback general de cambio
  onGuardarDatosUsuario,   // Función para guardar datos del usuario
  modoRenovar = false, 
}) => {

  // 🔹 Estado para controlar firma aceptada
  const [isFirmaAccepted, setIsFirmaAccepted] = useState(false);

  // 🔹 Estado de errores generales del paso 3
  const [errors, setErrors] = useState<FormErrors>({});

  // 🔹 Errores específicos del componente Datos1
  const [, setErrorsDatos1] = useState<FormErrors>({});

  // 🔹 Booleano que indica si hay errores en Datos1
  const [hasErrorsDatos1, setHasErrorsDatos1] = useState(false);

  // 🔹 Total a pagar calculado
  const [, setTotalPago] = useState<number>(0);

  // 🔹 Estado para almacenar firma en Base64
  const [firmaBase64, setFirmaBase64] = useState<string | null>(null);

  // 🔹 Referencia al componente de contrato PDF
  const contratoRef = useRef<ContratoPdfRef>(null);


  const handlePaymentChange = (option: PaymentOption, amount: number) => {
    setPaymentOption?.(option);   // actualiza opción de pago
    setPaymentAmount?.(amount);   // actualiza monto
  };
  

  // 🔹 Estado de datos adicionales (Paso 2)
  const [formData2, setFormData2] = useState<FormData2>({
    selectedProfession: [],
    payment: 0,
    paymentOption: "Anual",
  });
  

  // 🔹 Validamos Datos1 al montar el componente
  useEffect(() => {
    validateDatos1();
  }, []);

  // 🔹 Manejador general de cambios en inputs
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData1?.({
      ...formData1,
      [name]: value, // Actualizamos el campo correspondiente
    });
  };

  // 🔹 Efecto para debug: imprime opción de pago
  useEffect(() => {
   
  }, [paymentOption]);

  // 🔹 Función para validar los campos de Datos1
  const validateDatos1 = () => {
    let newErrors: FormErrors = {};

    // Validación de tipo de identificación
    if (!formData1?.idSelectDniNie) {
      newErrors.idSelectDniNie = true;
    }

    // Validaciones específicas para empresa
    if (isEmpresa) {
      if (
        !formData1?.cif ||
        formData1.cif.trim() === "" ||
        !ValidateCIF(formData1.cif)
      ) {
        newErrors.cif = true;
      }
    } else { // Validaciones para persona física
      if (formData1?.dniNie) {
        newErrors.dniNie = true;
      } else if (formData1?.idSelectDniNie === "dni" && !ValidateDNI(formData1?.dniNie!)) {
        newErrors.dniNie = true;
      } else if (formData1?.idSelectDniNie === "nie" && !ValidateNIE(formData1?.dniNie!)) {
        newErrors.dniNie = true;
      }
    }

    // Validaciones generales
    if (!isValidPhone(formData1?.numero_telefono || "")) {
      newErrors.numero_telefono = true;
    }

    if (!isValidEmail(formData1?.email || "")) {
      newErrors.email = true;
    }

    if (!isValidPostalCode(formData1?.postalCode || "")) {
      newErrors.postalCode = true;
    }

    setErrors(newErrors); // Guardamos errores
    return Object.keys(newErrors).length === 0; // Retornamos si hay errores o no
  };

  // 🔹 Función para cambiar tipo de identificación
  const handleIdTypeChange = (selectedType: string) => {
    setFormData1?.((prevData) => ({
      ...prevData!,
      idSelectDniNie: selectedType,
    }));
  };

  // 🔹 Función que prepara suscripción y guarda datos de usuario si no existen
  const prepararSuscripcion = async () => {
    if (!formData1?.profesional_id && onGuardarDatosUsuario) {
      await onGuardarDatosUsuario(); // Actualiza formData1 con IDs generados
    }
  };

  // 🔹 Parámetro de URL para editar perfil profesional
  const { perfil_profesional_id, profesional_id } = useParams<{ perfil_profesional_id: string; profesional_id: string }>();


  // 🔹 useEffect para cargar datos del perfil si se está editando
  useEffect(() => {
    if (!perfil_profesional_id || !profesional_id) return;
  
    setFormData1?.({
      ...formData1,
      profesional_id: Number(profesional_id),
      perfil_profesional_id: Number(perfil_profesional_id),
    });
  }, [perfil_profesional_id, profesional_id]);
  

  // 🔹 Renderizado del formulario Paso 3
  return (
    <div className="px-2 grid grid-cols-12 gap-4">

      {/* 🔹 Sección Datos1 */}
      <Datos1
        formData1={formData1}
        isFirmaAccepted={isFirmaAccepted}
        isEmpresa={isEmpresa}
        onChange={handleInputChange}
        onIdTypeChange={handleIdTypeChange}
        setErrors={setErrors}
        setFormData1={setFormData1}
        setErrorsDatos1={setErrorsDatos1}
        setHasErrorsDatos1={setHasErrorsDatos1}
        readOnly={!!perfil_profesional_id}
      />



      {/* 🔹 Sección Datos2 */}
      <Datos2
        selectedProfesiones={selectedProfesiones}
        handleProfesionSelect={handleProfesionSelect}
        paymentOption={paymentOption}
        paymentAmount={paymentAmount}
        setPaymentDetails={handlePaymentChange}
        setPaymentAmount={setPaymentAmount}
        onPaymentChange={handlePaymentChange}
        formData1={formData1}
        setFormData1={setFormData1}
        setPaymentOption={setPaymentOption}
        isFirmaAccepted={isFirmaAccepted}
        errors={errors}
        onChange={handleInputChange}
        setErrors={setErrors}
        setErrorsDatos1={setErrorsDatos1}
        setHasErrorsDatos1={setHasErrorsDatos1}
        readOnly={!!perfil_profesional_id}
      />

      {/* 🔹 Sección Datos3 */}
      <Datos3
        handleProfesionSelect={handleProfesionSelect}
        paymentOption={paymentOption}
        paymentAmount={paymentAmount}
        setPaymentDetails={setPaymentDetails}
        formData1={formData1}
        onFirmaAccept={(dataURL: React.SetStateAction<string | null>) => {
          setFirmaBase64(dataURL);
          setIsFirmaAccepted(true);
        }}
        onPaymentChange={(option: PaymentOption, amount: number) => {
          setPaymentOption?.(option); // ✅ ya es del tipo correcto
          setPaymentAmount?.(amount);
        }}
        setFormData2={setFormData2}
        isFirmaAccepted={isFirmaAccepted}
        isEmpresa={isEmpresa}
        selectedProfesiones={selectedProfesiones}
        setFormData1={setFormData1}
        onChange={onChange}
        errors={errors}
        setErrorsDatos1={setErrorsDatos1}
        setHasErrorsDatos1={setHasErrorsDatos1}
        hasErrorsDatos1={hasErrorsDatos1}
        contratoRef={contratoRef}
        firmaBase64={firmaBase64}
      />

      {/* 🔹 Sección Datos4 */}
      <Datos4
        isFirmaAccepted={isFirmaAccepted}
        selectedProfesiones={selectedProfesiones}
        paymentAmount={paymentAmount}
        setTotalPago={setTotalPago}
        contratoRef={contratoRef}
        firmaBase64={firmaBase64}
        formData1={formData1}
        formData2={formData2}
        paymentOption={paymentOption}
        isEmpresa={isEmpresa}
        onGuardarDatosUsuario={onGuardarDatosUsuario}
        idUsuario={formData1?.usuario_id}
        onPrepararSuscripcion={prepararSuscripcion}
        onPaymentChange={(option: PaymentOption, amount: number) => {
          setPaymentOption?.(option);
          setPaymentAmount?.(amount);
        }}
        modoRenovar={modoRenovar}
      />


    </div>
  );
};

// Exportamos el componente
export default FormularioPaso3;
